import webview from '@adapter/webview';
import { isEmpty, postWindowMessage } from '@groww-tech-pvt/ella';
import { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import queryString from 'query-string';
import { v4 as uuidv4 } from 'uuid';

import { generateChecksum, getPayloadData, getQueryString, isValidPayload, logoutUser } from './helpers';

import {clearNkeySeedAndSocketToken, getAuthToken, getDeviceUUID, getPinToken, getRefreshToken, getWebviewDeviceID, resetPinToken, setAuthToken, setRefreshToken} from 'src/utils/storage';

import { ROUTES } from '../../constants/routes';
import { getLoginPageURL, setRedirectionUrl } from '../../helpers';
import { RefreshSubscriber, RefreshTokenResponse } from 'src/utils/types';
import { refreshToken } from 'src/utils/services';
import { WEBVIEW_EVENTS_ACTION_TYPES } from 'src/utils/constants';
import { STORAGE_CONSTANTS } from 'src/utils/storage/constant';
import { clearSpecificCookiesWithParentDomain } from 'src/utils/storage/helper';
import { axiosInstance } from '..';
import { userDataStore } from '../../store';

const WV = webview();

interface AxiosRequestConfigWithMeta extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: number;
    urlTemplate?: string; // Template URL for metrics (e.g., '/api/users/${userId}')
  };
}

export const onAxiosInstanceNoSessionRequestFulfill = (
  config: any //ONTODO
) => {
  // Start timing for API response metrics
  // Preserve existing metadata (like urlTemplate) if it exists
  config.metadata = {
    ...config.metadata,
    startTime: Date.now(),
  };

  // if (!restrictChecksumUrls.some(el => config.url.includes(el))) {
  let payloadData: string | null = null;
  let queryStr = '';

  /*
    1. Get the device ID from local storage
    2. X-DEVICE-ID should not be in header if user is not logged in.
    3. Added a check for session if user is logged in and also deviceID is not empty then only pass it in headers.
  */
  const deviceID = WV.isActive ? getWebviewDeviceID() : getDeviceUUID();

  if (!isEmpty(deviceID) && config.headers) {
    config.headers['X-DEVICE-ID'] = deviceID; // adding deviceID to headers.
  }

  if (isValidPayload(config.data)) {
    payloadData = JSON.stringify(config.data);
  }

  if (isValidPayload(config.params)) {
    const paramString = queryString.stringify(config.params, {
      arrayFormat: 'comma',
      encode: false,
    }); // it will ignore if params value is undefined

    queryStr = paramString ? '?' + paramString : '';
  }

  const xReqId = uuidv4();

  if (config.headers) {
    config.headers['X-REQUEST-CHECKSUM'] = generateChecksum(
      decodeURIComponent(config.url ?? '') + queryStr,
      payloadData,
      xReqId
    );
    config.headers['X-REQUEST-ID'] = xReqId;
  }

  return config;
};

export const onAxiosInstanceRequestReject = (error: any) => {
  // Do something with request error
  return Promise.reject(error);
};


export const onAxiosInstanceRequestFulfill = (
  config: AxiosRequestConfigWithMeta
) => {
  // Start timing for API response metrics
  // Preserve existing metadata (like urlTemplate) if it exists
  config.metadata = {
    ...config.metadata,
    startTime: Date.now(),
  };

  if (config.headers) {
    config.headers.Authorization = getAuthToken();
    config.headers['X-USER-CAMPAIGN'] = getPinToken();
    config.headers['X-USER-ID'] = userDataStore.getState().userData?.user_account_id ?? '';
    // config.headers['x-hns-app-id'] = 'growwWeb';
    /*
        1. Get the device ID from local storage
        2. X-DEVICE-ID should not be in header if user is not logged in.
        3. Added a check for session if user is logged in and also deviceID is not empty then only pass it in headers.
      */
    const deviceID = WV.isActive ? getWebviewDeviceID() : getDeviceUUID();

    if (!isEmpty(deviceID)) {
      config.headers['X-DEVICE-ID'] = deviceID; // adding deviceID to headers.
    }

    // if (!restrictChecksumUrls.some(el => config.url.includes(el))) {
    const payloadData = getPayloadData(config);
    const queryStr = getQueryString(config);
    const xReqId = uuidv4();

    if (config.url) {
      config.headers['X-REQUEST-CHECKSUM'] = generateChecksum(
        decodeURIComponent(config.url) + queryStr,
        payloadData,
        xReqId
      );
    }

    config.headers['X-REQUEST-ID'] = xReqId;
  }

  return config;
};

export const onAxiosInstanceResponseFulfill = (response: AxiosResponse) => {
  return response;
};

export const onAxiosInstanceResponseReject = async (error: any) => {

  // if we enable advanced charts in msite in future, remember to remove this check
  // as getOSName ad isMobileDeviceCheck return true in case of chrome on android and iOS also.
  const originalRequest = error.config;

  // Check if we're on an unauthenticated route (like login page)
  // Don't try to refresh tokens on these routes to prevent infinite loops
  const isOnUnauthenticatedRoute =
    typeof window !== 'undefined' && window.location.pathname === ROUTES.LOGIN;

  // If we're on an unauthenticated route and get a 401, just reject without trying to refresh
  // This prevents infinite loops when API calls on login page return 401
  if (isOnUnauthenticatedRoute && error.response?.status === 401) {
    return Promise.reject(error);
  }

  /* If the error is a 401 Unauthorized error and the error code is also 401
      and this is not a retry attempt
      For Invalid Auth Token (AT has expired) -> Status code: 401 && Error code: 401
      For Invalid Refresh Token (RT has expired) -> Status code: 401 && Error code: 463
    */
  const isUnAuthorized =
    typeof error.response !== 'undefined' &&
    error.response.status === 401 &&
    error.response.data?.errorCode === 401 &&
    !originalRequest._retry;

  // Don't attempt token refresh on unauthenticated routes (double check for safety)
  if (isUnAuthorized && !isOnUnauthenticatedRoute) {
    // A flag to keep track of whether a refresh request is currently being made
    let isRefreshRequesting = false;
    // An array of subscribers(requests) waiting for an authentication token refresh
    let refreshSubscribers: RefreshSubscriber[] = [];
    let newRTvalue: string | null = null;

    const originalRequest = error.config;

    // If a refresh request is not currently being made
    if (!isRefreshRequesting) {
      // If there is no refresh token available, logout the user
      if (!getRefreshToken()) {
        logoutUser();
        return;
      }

      try {
        let authToken = getAuthToken();

        // If value of newRTvalue is null, we need to refresh the token | call refresh token api
        if (!newRTvalue) {
          isRefreshRequesting = true;
          const refreshData: RefreshTokenResponse = await refreshToken();

          const newAuthToken = refreshData?.data?.data?.authToken || '';
          const newRefreshToken = refreshData?.data?.data?.refreshToken || '';

          authToken = newAuthToken;

          // Update the local-storage with the new authentication and refresh tokens
          if (WV.isActive) {
            const postObj = {
              action: WEBVIEW_EVENTS_ACTION_TYPES.AT_RT_UPDATE,
              params: {
                at: newAuthToken,
                rt: newRefreshToken,
              },
            };

            // Delete tokens in cookie that might be present in sub domain
            // Refer RCA https://coda.io/d/Web-Team_d-LNk7c4rKF/Multiple-401-calls-at-webview_sutUl#_luKHF
            // Clear tokens across all domains and subdomains before setting new ones
            clearSpecificCookiesWithParentDomain([
              STORAGE_CONSTANTS.AUTHORIZATION,
              STORAGE_CONSTANTS.REFRESH_TOKEN,
            ]);

            setRefreshToken(`Bearer ${newRefreshToken}`);
            postWindowMessage(postObj);
          } else {
            setRefreshToken(newRefreshToken);
          }

          setAuthToken(newAuthToken, false);

          newRTvalue = newRefreshToken;
          isRefreshRequesting = false;

          // Setting the newRTvalue to null after 10 minutes
          setTimeout(() => {
            newRTvalue = null;
          }, 600000);
        }

        // Mark the original request as a retry attempt
        originalRequest._retry = true;

        // If there are any subscribers waiting for a token refresh, notify them with the new token
        if (refreshSubscribers.length > 0) {
          refreshSubscribers.forEach(cb => cb(authToken));

          // Reset the refresh subscribers array
          refreshSubscribers = [];
        }

        // Retry the original request with the new authentication token
        originalRequest.headers.Authorization = `Bearer ${authToken}`;
        return axiosInstance(originalRequest);
      } catch (error) {
        // Only logout if not on unauthenticated route to prevent infinite loops
        if (typeof window !== 'undefined' && !isOnUnauthenticatedRoute) {
          logoutUser();
        }
        return Promise.reject(error);
      }
    } else {
      /* If a refresh request is currently being made, add the original request
         to the list of subscribers waiting for a token refresh */
      return new Promise(resolve => {
        refreshSubscribers.push((newAuthToken: string) => {
          originalRequest.headers.Authorization = `Bearer ${newAuthToken}`;
          resolve(axiosInstance(originalRequest));
        });
      });
    }
  }

  const shouldLogoutUser =
    typeof error.response !== 'undefined' &&
    error.response.status === 401 &&
    error.response.data?.errorCode === 463;

  // Don't logout/redirect if already on login page to prevent infinite loops
  if (shouldLogoutUser && !isOnUnauthenticatedRoute) {
    if (typeof window !== 'undefined') {
      logoutUser();
    }
  }

  if (typeof error.response !== 'undefined' && error.response.status === 403) {
    if (typeof window !== 'undefined') {
      setRedirectionUrl('', true);
      resetPinToken();
      clearNkeySeedAndSocketToken();

      if (WV.isActive) {
        const postObj = {
          action: WEBVIEW_EVENTS_ACTION_TYPES.TWO_FACTOR_EXPIRE,
          params: null,
        };

        postWindowMessage(postObj);
      } else {
        window.location.href = getLoginPageURL();
      }
    }
  }

  return Promise.reject(error);
};
